import {
  Box,
  Divider,
  Flex,
  Form,
  type FormControlProp,
  HintIcon,
  MonetaryInputField,
  SubmitButton,
  Text,
} from '@/components';
import {
  FundAdditionalIncomeAndExpenditureSchema,
  type PeriodScopeType,
} from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import {
  useClericalExpenditureSummaryByTypeWatch,
  useExpenditureSummaryByTypeWatch,
  useIncomeAndExpenditureSummaryByTypeWatch,
  useIncomeSummaryByTypeWatch,
} from '@/features/project/projects/detail/income-and-expenditure/_hooks/fund-additional-income-and-expenditure';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { AmountUnitMultipliers } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { CalculatedLabeledText } from '../../../../../../../components/Form/CalculatedLabeledText';

type IncomeNamesAndLabels = Array<{
  label: string;
  fieldNames: Record<PeriodScopeType, keyof FormValues>;
}>;

type FormValues = z.infer<typeof FundAdditionalIncomeAndExpenditureSchema>;

type Props = {
  isBefore2025: boolean;
  type: PeriodScopeType;
  initialValues: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

type SummaryProps = {
  control: FormControlProp<FormValues>;
  type: PeriodScopeType;
};

export const FundAdditionalIncomeAndExpenditureForm: FC<Props> = ({
  isBefore2025,
  type,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(FundAdditionalIncomeAndExpenditureSchema),
  });

  const incomeLabelsAndNames: IncomeNamesAndLabels = [
    {
      label: t('granted-amount-by-the-country'),
      fieldNames: {
        'previous-year-performance': 'grantedAmountByTheCountry',
        'estimated-this-year': 'estimatedGrantedAmountByTheCountry',
        'estimated-next-year': 'estimatedGrantedAmountByTheCountryNextYear',
      },
    },
    {
      label: t('investment-income'),
      fieldNames: {
        'previous-year-performance': 'investmentIncome',
        'estimated-this-year': 'estimatedInvestmentIncome',
        'estimated-next-year': 'estimatedInvestmentIncomeNextYear',
      },
    },
    {
      label: t('national-equivalent-amount-of-that'),
      fieldNames: {
        'previous-year-performance': 'nationalEquivalentOfInvestmentIncome',
        'estimated-this-year': 'estimatedNationalEquivalentOfInvestmentIncome',
        'estimated-next-year':
          'estimatedNationalEquivalentOfInvestmentIncomeNextYear',
      },
    },
    {
      label: t('business-income'),
      fieldNames: {
        'previous-year-performance': 'businessIncome',
        'estimated-this-year': 'estimatedBusinessIncome',
        'estimated-next-year': 'estimatedBusinessIncomeNextYear',
      },
    },
    {
      label: t('national-equivalent-amount-of-that'),
      fieldNames: {
        'previous-year-performance': 'nationalEquivalentOfBusinessIncome',
        'estimated-this-year': 'estimatedNationalEquivalentOfBusinessIncome',
        'estimated-next-year':
          'estimatedNationalEquivalentOfBusinessIncomeNextYear',
      },
    },
    {
      label: t('others'),
      fieldNames: {
        'previous-year-performance': 'otherIncomeAmount',
        'estimated-this-year': 'estimatedOtherIncomeAmount',
        'estimated-next-year': 'estimatedOtherIncomeAmountNextYear',
      },
    },
  ];

  const expenditureLabelsAndNames: IncomeNamesAndLabels = [
    {
      label: t('business-expenses'),
      fieldNames: {
        'previous-year-performance': 'businessExpense',
        'estimated-this-year': 'estimatedBusinessExpense',
        'estimated-next-year': 'estimatedBusinessExpenseNextYear',
      },
    },
    {
      label: t('administrative-expenses'),
      fieldNames: {
        'previous-year-performance': 'administrativeExpense',
        'estimated-this-year': 'estimatedAdministrativeExpense',
        'estimated-next-year': 'estimatedAdministrativeExpenseNextYear',
      },
    },
    {
      label: t('chore-expenses-of-fund-establishing-of-that'),
      fieldNames: {
        'previous-year-performance': 'choreExpensesOfFundEstablishing',
        'estimated-this-year': 'estimatedChoreExpensesOfFundEstablishing',
        'estimated-next-year':
          'estimatedChoreExpensesOfFundEstablishingNextYear',
      },
    },
    {
      label: t('personnel-expenses-of-fund-establishing-of-that'),
      fieldNames: {
        'previous-year-performance': 'personnelExpensesOfFundEstablishing',
        'estimated-this-year': 'estimatedPersonnelExpensesOfFundEstablishing',
        'estimated-next-year':
          'estimatedPersonnelExpensesOfFundEstablishingNextYear',
      },
    },
  ];

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Flex vertical gap={16}>
        <Text
          variant="h4"
          text={
            type === 'previous-year-performance'
              ? t('previous-fiscal-year-performance')
              : type === 'estimated-this-year'
                ? t('current-fiscal-year-estimation')
                : t('next-fiscal-year-estimation')
          }
        />
        <Box>
          <Flex vertical>
            <MonetaryInputField
              name={
                type === 'estimated-next-year'
                  ? 'beginningOfTheEstimatedFundBalanceNextYear'
                  : 'beginningOfTheFundBalance'
              }
              label={t('fund-balance-at-the-end-of-previous-fiscal-year')}
              control={control}
              errors={errors}
              unit={unit}
              disabled={
                !isBefore2025 &&
                ((type !== 'estimated-next-year' && !!initialValues.prior) ||
                  type === 'estimated-next-year')
              }
            />
          </Flex>

          <ThickDivider />

          <Flex vertical gap={4}>
            <Box marginBottom={4}>
              <Flex align="center" gap={4}>
                <Text variant="h5" text={t('income')} />
                <HintIcon hint={t('hint-fund-income')} />
              </Flex>
            </Box>

            {incomeLabelsAndNames.map((field, index) => (
              <MonetaryInputField
                // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                key={index}
                name={field.fieldNames[type]}
                label={field.label}
                control={control}
                errors={errors}
                unit={unit}
              />
            ))}

            <CalculatedIncomeSummaryLabeledText type={type} control={control} />
          </Flex>

          <ThickDivider />

          <Flex vertical gap={4}>
            <Box marginBottom={4}>
              <Flex align="center" gap={4}>
                <Text variant="h5" text={t('expense')} />
                <HintIcon hint={t('hint-fund-expenditure')} />
              </Flex>
            </Box>

            {expenditureLabelsAndNames.map((field, index) => (
              <MonetaryInputField
                // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                key={index}
                name={field.fieldNames[type]}
                label={field.label}
                control={control}
                errors={errors}
                unit={unit}
              />
            ))}

            <CalculatedExpenditureSummaryLabeledText
              type={type}
              control={control}
            />
          </Flex>

          <ThickDivider />

          <Flex vertical gap={4}>
            <MonetaryInputField
              name={
                type === 'previous-year-performance'
                  ? 'amountReturnedToTheNationalTreasury'
                  : type === 'estimated-this-year'
                    ? 'estimatedAmountReturnedToTheNationalTreasury'
                    : 'estimatedAmountReturnedToTheNationalTreasuryNextYear'
              }
              label={t('returned-to-the-national-treasury')}
              control={control}
              errors={errors}
              unit={unit}
              labelSize="large"
            />

            <MonetaryInputField
              name={
                type === 'previous-year-performance'
                  ? 'amountReturnedToTheOrganization'
                  : type === 'estimated-this-year'
                    ? 'estimatedAmountReturnedToTheOrganization'
                    : 'estimatedAmountReturnedToTheOrganizationNextYear'
              }
              label={t('returned-to-the-organization')}
              control={control}
              errors={errors}
              unit={unit}
              labelSize="large"
            />

            <CalculatedIncomeAndExpenditureSummaryLabeledText
              control={control}
              type={type}
            />

            <MonetaryInputField
              name={
                type === 'previous-year-performance'
                  ? 'nationalEquivalentOfTheBalance'
                  : type === 'estimated-this-year'
                    ? 'estimatedNationalEquivalentOfTheBalance'
                    : 'estimatedNationalEquivalentOfTheBalanceNextYear'
              }
              label={t('national-equivalent-amount-of-that')}
              control={control}
              errors={errors}
              unit={unit}
              labelSize="large"
            />
          </Flex>

          <ThickDivider />

          <Flex vertical gap={4}>
            <Box marginBottom={4}>
              <Text variant="h5" text={t('clerical-personnel-expenses')} />
            </Box>

            <MonetaryInputField
              name={
                type === 'previous-year-performance'
                  ? 'choreExpensesNotFromThisFund'
                  : type === 'estimated-this-year'
                    ? 'estimatedChoreExpensesNotFromThisFund'
                    : 'estimatedChoreExpensesNotFromThisFundNextYear'
              }
              label={t('chore-expense')}
              control={control}
              errors={errors}
              unit={unit}
            />

            <MonetaryInputField
              name={
                type === 'previous-year-performance'
                  ? 'personnelExpensesNotFromThisFund'
                  : type === 'estimated-this-year'
                    ? 'estimatedPersonnelExpensesNotFromThisFund'
                    : 'estimatedPersonnelExpensesNotFromThisFundNextYear'
              }
              label={t('personnel-expense')}
              control={control}
              errors={errors}
              unit={unit}
            />

            <CalculatedClericalExpenditureSummaryLabeledText
              type={type}
              control={control}
            />
          </Flex>
        </Box>
      </Flex>
      <Box marginTop={12}>
        <SubmitButton text={t('save')} loading={isSubmitting} />
      </Box>
    </Form>
  );
};

const ThickDivider = () => (
  <Divider style={{ borderWidth: '2px', marginTop: 0 }} size="lg" />
);

/**
 * 収入合計
 */
const CalculatedIncomeSummaryLabeledText = ({
  type,
  control,
}: SummaryProps) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const value = useIncomeSummaryByTypeWatch({ type, control })?.div(
    AmountUnitMultipliers[unit],
  );

  return (
    <CalculatedLabeledText
      title={t('total-amount')}
      value={value?.toFixed()}
      unit={unit}
    />
  );
};

/**
 * 支出合計
 */
const CalculatedExpenditureSummaryLabeledText = ({
  type,
  control,
}: SummaryProps) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const value = useExpenditureSummaryByTypeWatch({ type, control })?.div(
    AmountUnitMultipliers[unit],
  );

  return (
    <CalculatedLabeledText
      title={t('total-amount')}
      value={value?.toFixed()}
      unit={unit}
    />
  );
};

/**
 * 当年度末基金残高
 */
const CalculatedIncomeAndExpenditureSummaryLabeledText = ({
  type,
  control,
}: SummaryProps) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const value = useIncomeAndExpenditureSummaryByTypeWatch({
    type,
    control,
  })?.div(AmountUnitMultipliers[unit]);

  return (
    <CalculatedLabeledText
      title={t('fund-balance-at-the-end-of-fiscal-year')}
      value={value?.toFixed()}
      size="large"
      unit={unit}
    />
  );
};

/**
 * 事務・人件費合計
 */
const CalculatedClericalExpenditureSummaryLabeledText: FC<SummaryProps> = ({
  type,
  control,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const value = useClericalExpenditureSummaryByTypeWatch({
    type,
    control,
  })?.div(AmountUnitMultipliers[unit]);

  return (
    <CalculatedLabeledText
      title={t('total-amount')}
      value={value?.toFixed()}
      unit={unit}
    />
  );
};
